"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { deviceDetector } from "@ospweb/sdk-ui";
import { cn } from "utils";
import type { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import { useHotNewsBySubTag } from "../hooks/useHotNewsBySubTag";
import { AIInsightDocsGallery } from "@components/AIInsight/AIInsightDocsGallery";
import { Footer } from "@components/TokenDetails/Footer";
import type { AIInsightNewsPageData } from "./types";
import { ScrollRestorerScrollableView } from "@/src/mobiles/mainContainer/ScrollRestorerView";

type AIInsightNewsSubTagPageProps = {
  /** 频道 slug（8 大频道固定值之一） */
  category: string;
  /** 二级子标签 slug（来自 themes/ranking.slug） */
  subTag: string;
  /** 二级主题 ID，与 getRankingContents 的 themeIds 一致 */
  subTagId: string;
  locale: string;
  deviceType: DeviceType;
  /** 服务端首屏数据（已按 subTag 过滤） */
  initialDocsData: AIInsightNewsPageData;
};

/**
 * News 二级子标签页面（news/{category}/{subTag}）。
 * - 选中态由 URL path 参数驱动（不读取 query/theme）
 * - 内容列表复用 AIInsightPage 的画廊容器/虚拟滚动方案
 */
export const AIInsightNewsSubTagPage = (
  props: AIInsightNewsSubTagPageProps,
) => {
  const {
    category,
    subTagId,
    locale,
    deviceType: serverDeviceType,
    initialDocsData,
  } = props;

  // 客户端 UA 动态检测，避免移动端布局闪动
  const [deviceType, setDeviceType] = useState<"mobile" | "desktop">(
    serverDeviceType === "desktop" ? "desktop" : "mobile",
  );
  const [isClientReady, setIsClientReady] = useState(false);

  useEffect(() => {
    const clientDeviceType = deviceDetector.detectDeviceTypeFromUA();
    setDeviceType(clientDeviceType);
    setIsClientReady(true);
  }, [serverDeviceType]);

  /** 与 PC 布局一致：外层可视宽度左右各预留 60，共 120；桌面固定 4 列、列间距见下方 DESKTOP_COLUMN_GAP */
  const DESKTOP_OUTER_WIDTH_RESERVE = 120;
  const DESKTOP_COLUMN_COUNT = 4;
  const DESKTOP_COLUMN_GAP = 32;

  const pageShellRef = useRef<HTMLDivElement>(null);
  // 移动端动态计算 itemWidth，确保始终显示两列
  const galleryContainerRef = useRef<HTMLDivElement>(null);
  const [mobileItemWidth, setMobileItemWidth] = useState(172);
  const [desktopItemWidth, setDesktopItemWidth] = useState(251);

  useEffect(() => {
    if (deviceType !== "mobile" || !galleryContainerRef.current) {
      return;
    }

    const updateItemWidth = () => {
      if (!galleryContainerRef.current) {
        return;
      }

      const containerWidth = galleryContainerRef.current.clientWidth;
      const padding = 0; // AIInsightDocsGallery 的 padding 是 0
      const columnGap = 16; // 移动端的 columnGap
      const columns = 2; // 固定显示两列

      const calculatedWidth =
        (containerWidth - padding * 2 - columnGap * (columns - 1)) / columns;

      const minWidth = 140;
      const finalWidth = Math.max(calculatedWidth, minWidth);
      setMobileItemWidth(Math.floor(finalWidth));
    };

    updateItemWidth();

    const resizeObserver = new ResizeObserver(updateItemWidth);
    resizeObserver.observe(galleryContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [deviceType]);

  useEffect(() => {
    if (deviceType !== "desktop" || !pageShellRef.current) {
      return;
    }

    const updateDesktopItemWidth = () => {
      const el = pageShellRef.current;
      if (!el) {
        return;
      }
      const W = el.clientWidth;
      if (W <= 0) {
        return;
      }
      // itemWidth = (外层宽度 - 120 - gap×3) / 4，保证 4 列排布
      const gapCount = DESKTOP_COLUMN_COUNT - 1;
      const raw =
        (W - DESKTOP_OUTER_WIDTH_RESERVE - gapCount * DESKTOP_COLUMN_GAP) /
        DESKTOP_COLUMN_COUNT;
      setDesktopItemWidth(Math.max(1, Math.floor(raw)));
    };

    updateDesktopItemWidth();
    const ro = new ResizeObserver(updateDesktopItemWidth);
    ro.observe(pageShellRef.current);
    return () => ro.disconnect();
  }, [deviceType]);

  const itemWidth = useMemo(() => {
    return deviceType === "mobile" ? mobileItemWidth : desktopItemWidth;
  }, [deviceType, mobileItemWidth, desktopItemWidth]);

  const [isClientInit, setIsClientInit] = useState(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useHotNewsBySubTag({
      subTagId,
      locale,
      limit: initialDocsData?.pageSize || 20,
      initialData: isClientInit ? undefined : initialDocsData,
      enabled: !initialDocsData?.data?.length,
    });

  useEffect(() => {
    setIsClientInit(true);
  }, []);

  return (
    <div
      ref={pageShellRef}
      className={cn(
        "flex flex-col w-full items-center overflow-hidden md:rounded-t-4 md:px-15 px-4",
        deviceType !== "mobile"
          ? "h-[calc(100dvh-60px)]"
          : "h-[calc(100dvh-100px)]",
      )}
      style={{
        visibility: isClientReady ? "visible" : "hidden",
        opacity: isClientReady ? 1 : 0,
        transition: "opacity 0.2s ease-in-out",
      }}
    >
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col w-full",
          deviceType !== "mobile" ? "border-border_white_transparent10" : "",
        )}
      >
        <div className="flex flex-1 min-h-0 flex-col overflow-hidden no-scrollbar w-full">
          <ScrollRestorerScrollableView
            ref={galleryContainerRef}
            className="min-h-0 flex-1 overflow-y-auto no-scrollbar"
            style={{ overscrollBehavior: "contain" }}
          >
            {/* TODO: 将列表卡片详情链接改为 `news/{post}/[title]`（本期先实现 SEO/列表）。 */}
            <AIInsightDocsGallery
              data={data}
              category={category}
              locale={locale}
              itemWidth={itemWidth}
              itemHeight={deviceType === "mobile" ? 189 : 245}
              enableInfiniteScroll={true}
              desktopGridColumns={
                deviceType === "desktop" ? DESKTOP_COLUMN_COUNT : undefined
              }
              className="w-full"
              columnGap={deviceType === "mobile" ? 16 : DESKTOP_COLUMN_GAP}
              rowGap={deviceType === "mobile" ? 16 : 24}
              padding={0}
              preloadItemCount={
                deviceType === "mobile"
                  ? 10
                  : data?.length > 15
                    ? 15
                    : data?.length
              }
              deviceType={deviceType}
              isLoading={isLoading}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />
            <Footer locale={locale} className="mt-6 md:hidden" />
          </ScrollRestorerScrollableView>
        </div>
      </div>
    </div>
  );
};
